'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { Link } from '@repo/web-ui/components/link'
import { useFeedRoute } from '../../../contexts/feed-route-context'

import { IconBookmarks, IconPerson, IconPhotoPrints } from '@repo/design-system-cf/components/icons'

export type ActiveMenuType = 'discover' | 'my-profile' | 'saved-posts' | 'clubhouses'

export function MobileMenu({ activeMenu, slug }: { activeMenu?: ActiveMenuType; slug?: string }) {
  const t = useTranslations('social-feed.components.feed_user_profile_panel.links')
  const user = useUser()
  const { openModal } = useUserAuthModal()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { basePath } = useFeedRoute()

  const handleProtectedClick = (e: React.MouseEvent) => {
    if (!isLoggedIn) {
      e.preventDefault()
      openModal()
      return
    }
  }

  const isMyProfileActive = activeMenu === 'my-profile' && slug === user?.data?.slug

  const menuItems = [
    {
      key: 'my-profile',
      label: t('my_profile'),
      href: `/crafter/${user?.data?.slug}/`,
      icon: IconPerson,
      protected: true,
      isVisible: true,
      isActive: isMyProfileActive,
    },
    {
      key: 'discover',
      label: t('projects_feed'),
      href: `${basePath}/`,
      icon: IconPhotoPrints,
      isVisible: true,
      isActive: activeMenu === 'discover',
    },
    {
      key: 'saved-posts',
      label: t('saved_posts'),
      href: `${basePath}/saved/`,
      icon: IconBookmarks,
      protected: true,
      isVisible: true,
      isActive: activeMenu === 'saved-posts' && isLoggedIn,
    },
  ]

  return (
    <>
      <div className="bg-cf-white fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center border-t-[1px] border-t-[#0A0A0B1A] lg:hidden rounded-t-4 border border-[#1313151F] shadow-[0px_1px_2px_0px_#1313151F,0px_4px_8px_3px_#13131529]">
        <div className="flex w-full max-w-[600px] flex-row justify-between px-2 py-1">
          {menuItems.map(
            ({ key, label, href, icon: Icon, protected: isProtected, isVisible, isActive }) =>
              isVisible && (
                <Link
                  key={key}
                  href={href}
                  onClick={isProtected ? handleProtectedClick : undefined}
                  className={tw(
                    'relative flex h-70 sm:h-[88px] w-[87px] sm:w-[118px] flex-none flex-col items-center justify-center gap-0.5 rounded-2 bg-transparent sm:px-4 p-2',
                    'text-cf-sf-gray-1150 leading-4',
                    isActive
                      ? 'font-bold text-cf-purple-900 bg-cf-purple-50'
                      : 'text-cf-sf-gray-1100',
                  )}
                >
                  {typeof Icon === 'function' ? <Icon className="tablet:size-8 size-6" /> : Icon}
                  <span className="text-11 leading-3 sm:text-14 sm:leading-5 text-center">
                    {label}
                  </span>
                </Link>
              ),
          )}
        </div>
      </div>
    </>
  )
}
